import { intlLocale } from '@/i18n';

// Central date/time formatting for the admin.
//
// We follow the admin's UI language (en-GB / nl-NL, see @/i18n), so month
// names and day/month order match the rest of the screen. We always force a
// 24-hour clock (`hour12: false`): the admin is used in Europe, where a 12h
// AM/PM clock reads as wrong. Centralizing this also means every timestamp
// across the app is consistent.

// Formatters per UI language, built on first use: the admin can switch
// language without a reload, so none may be fixed at module load.
const cache = new Map<string, Intl.DateTimeFormat>();

type Kind = 'dateTime' | 'date' | 'time' | 'day' | 'dayYear';

function formatter(kind: Kind): Intl.DateTimeFormat {
    const locale = intlLocale();
    const key = `${kind}:${locale}`;
    let fmt = cache.get(key);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat(locale, OPTIONS[kind]);
        cache.set(key, fmt);
    }
    return fmt;
}

const OPTIONS: Record<Kind, Intl.DateTimeFormatOptions> = {
    dateTime: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false },
    date: { year: 'numeric', month: 'short', day: '2-digit' },
    time: { hour: '2-digit', minute: '2-digit', hour12: false },
    day: { weekday: 'short', day: 'numeric', month: 'short' },
    dayYear: { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
};

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
    if (value == null || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** Date + 24h time, e.g. "12 Jun 2026, 14:30". Returns the placeholder for empty/invalid input. */
export function formatDateTime(value: DateInput, placeholder = '—'): string {
    const d = toDate(value);
    return d ? formatter('dateTime').format(d) : placeholder;
}

/** Date only, e.g. "12 Jun 2026". Returns the placeholder for empty/invalid input. */
export function formatDate(value: DateInput, placeholder = '—'): string {
    const d = toDate(value);
    return d ? formatter('date').format(d) : placeholder;
}

/**
 * A calendar day with its weekday, e.g. "Thu 22 Oct" (or "Thu 22 Oct 2026"
 * with `withYear`), for the API's "YYYY-MM-DD" days. The day is read as
 * that day here, not as midnight UTC, so no timezone can move it to the
 * day before. Returns an empty string for empty/invalid input.
 */
export function formatDay(ymd: string | null | undefined, withYear = false): string {
    const m = ymd ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null;
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return formatter(withYear ? 'dayYear' : 'day').format(d);
}

/** 24h time only, e.g. "14:30". Returns the placeholder for empty/invalid input. */
export function formatTime(value: DateInput, placeholder = '—'): string {
    const d = toDate(value);
    return d ? formatter('time').format(d) : placeholder;
}
